/**
 * "Don't ask for a code on this computer for 30 days" — v3.91.0.
 *
 * Asked for by the owner (2026-10-06; settled 2026-10-07): as the only global administrator he
 * was asked for an MFA code at every sign-in on his own laptop. The 30-minute
 * inactivity sign-out stays exactly as it is; only the code prompt goes away
 * on a computer he chose to trust.
 *
 * How: a trusted computer is enrolled as one more TOTP second factor on the
 * account, named "Trusted computer · <browser> on <system> · until <date>".
 * Its secret never leaves the browser in readable form — it is imported at
 * enrollment as a non-extractable WebCrypto key and kept in IndexedDB
 * (trustedComputerStore.js). When Firebase asks for a second factor at
 * sign-in and this browser holds the key of a factor on that account, the
 * browser computes the code and finishes the sign-in itself. Firebase still
 * verifies both factors; no server code, rule or token changes.
 *
 * Limits, on purpose:
 *  - Global administrator only (the `admin` claim), and only right after a
 *    sign-in WITH a code (Firebase requires a recent sign-in to enroll).
 *  - 30 days from enrollment, by the server's enrollment time. Past that the
 *    computer asks for a code again; the expired factor is removed after a
 *    later sign-in with a code, from any computer — unless it is the
 *    account's newest factor (see below), which waits for a removal by hand.
 *  - The 30 days are kept by the app, not by Firebase: someone who copies
 *    the browser profile and drives it with developer tools could keep
 *    answering until the factor is removed. Removing it (Admin → Security
 *    Settings) is what revokes a computer.
 *  - The code screen never offers a trusted computer as the factor to type a
 *    code for; the admin MFA gate does not count trusted computers as MFA.
 *
 * Firebase side effects (documented behaviour):
 *  - enrolling a factor emails the account and revokes the account's OTHER
 *    sessions;
 *  - removing one emails the account, and removing the MOST RECENTLY ADDED
 *    factor signs the current session out (auth/user-token-expired). So the
 *    app never removes the newest factor in the background: a renewal adds
 *    the new trusted computer first and removes the old one after, and an
 *    expired trusted computer that is still the newest factor waits until it
 *    is not (or is removed by hand, with a warning, in Security Settings).
 */
import { multiFactor, TotpMultiFactorGenerator } from 'firebase/auth';
import {
  base32ToBytes, defaultSubtle, importTotpKey, totpCode, webCryptoHashName,
} from './totp';
import { indexedDbStore } from './trustedComputerStore';
import { logSecurityEvent } from '../services/Logger';
import { auth } from '../firebase';

export const TRUST_DAYS = 30;
export const TRUST_MS = TRUST_DAYS * 24 * 60 * 60 * 1000;
export const TRUSTED_COMPUTER_PREFIX = 'Trusted computer';

export class TrustedComputerError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'TrustedComputerError';
    this.code = code;
  }
}

function deps(overrides = {}) {
  return {
    store: indexedDbStore,
    subtle: defaultSubtle(),
    now: () => Date.now(),
    mfa: { multiFactor, TotpMultiFactorGenerator },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    log: logSecurityEvent,
    // Who the session belongs to NOW (the boot pipeline may have signed the
    // sign-in out since) — nothing is changed for a session that is gone.
    currentUid: () => (auth && auth.currentUser ? auth.currentUser.uid : null),
    ...overrides,
  };
}

/** security_logs writes never hold up the steps around them (a stalled Firestore channel). */
function logQuietly(d, ...args) {
  Promise.resolve().then(() => d.log(...args)).catch(() => {});
}

/** At most `ms` for a store call that might never settle. */
function bounded(promise, ms = 3000) {
  let timer;
  return Promise.race([promise, new Promise((resolve) => { timer = setTimeout(resolve, ms); })]).finally(() => clearTimeout(timer));
}

/** Is this enrolled factor (or sign-in hint) a trusted computer? */
export function isTrustedComputerFactor(info) {
  return !!info
    && info.factorId === 'totp'
    && typeof info.displayName === 'string'
    && info.displayName.startsWith(TRUSTED_COMPUTER_PREFIX);
}

/** The factor's enrollment time (server clock) in ms, or null when missing or unreadable. */
export function enrolledAtMs(info) {
  const t = Date.parse(info && info.enrollmentTime);
  return Number.isFinite(t) ? t : null;
}

/** When a trusted computer stops answering: enrollment (server time) + 30 days. null when unknown. */
export function trustedUntilMs(info) {
  const at = enrolledAtMs(info);
  return at == null ? null : at + TRUST_MS;
}

/** The hints the code screen may ask for — every factor except trusted computers. */
export function usableHints(hints) {
  return (Array.isArray(hints) ? hints : []).filter((h) => h && !isTrustedComputerFactor(h));
}

/** The factor the code screen asks for: an authenticator app, then SMS, then anything else — never a trusted computer. */
export function pickManualHint(hints) {
  const usable = usableHints(hints);
  return usable.find((h) => h.factorId === 'totp') || usable.find((h) => h.factorId === 'phone') || usable[0] || null;
}

/** "Chrome on Mac" from a user-agent string. */
export function describeBrowser(userAgent) {
  const s = String(userAgent || '');
  let browser = 'Browser';
  if (/Edg\//.test(s)) browser = 'Edge';
  else if (/OPR\//.test(s)) browser = 'Opera';
  else if (/Firefox\/|FxiOS/.test(s)) browser = 'Firefox';
  else if (/Chrome\/|CriOS/.test(s)) browser = 'Chrome';
  else if (/Safari\//.test(s)) browser = 'Safari';
  let system = 'computer';
  if (/iPhone/.test(s)) system = 'iPhone';
  else if (/iPad/.test(s)) system = 'iPad';
  else if (/Android/.test(s)) system = 'Android';
  else if (/CrOS/.test(s)) system = 'Chromebook';
  else if (/Macintosh|Mac OS X/.test(s)) system = 'Mac';
  else if (/Windows/.test(s)) system = 'Windows';
  else if (/Linux/.test(s)) system = 'Linux';
  return `${browser} on ${system}`;
}

/** "Nov 6, 2026". */
export function formatTrustDate(ms) {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The factor's display name — what Security Settings and Firebase's emails show. */
export function trustedComputerName(nowMs, userAgent) {
  return `${TRUSTED_COMPUTER_PREFIX} · ${describeBrowser(userAgent)} · until ${formatTrustDate(nowMs + TRUST_MS)}`;
}

/**
 * The account's most recently added factor — the one whose removal signs the
 * session out — by enrollment time. null when any factor's time is missing
 * or unreadable (then nobody can tell which is newest).
 */
export function newestFactor(factors) {
  const list = Array.isArray(factors) ? factors : [];
  let newest = null;
  let newestAt = -Infinity;
  for (const f of list) {
    const at = enrolledAtMs(f);
    if (at == null) return null;
    if (at >= newestAt) { newest = f; newestAt = at; } // a tie: the later in the list
  }
  return newest;
}

/**
 * Could removing `factor` sign this session out? True when it is the newest
 * factor — and, to be safe, whenever the newest cannot be told.
 */
export function isNewestFactor(factors, factor) {
  if (!factor) return false;
  const list = Array.isArray(factors) ? factors : [];
  if (list.length === 0) return false;
  const newest = newestFactor(list);
  return newest == null || newest.uid === factor.uid;
}

/**
 * After Firebase answered a removal with auth/user-token-expired (it ends the
 * session when the newest factor goes — or because the session had already
 * been revoked elsewhere): did the factor actually go? The SDK drops it from
 * enrolledFactors only after the server confirmed the removal.
 */
export function factorStillEnrolled(factors, factor) {
  return (Array.isArray(factors) ? factors : []).some((f) => f && factor && f.uid === factor.uid);
}

const OFFER_KEY = 'literacy-app:trustedComputerOffer:v1';

/**
 * The checkbox is offered only in a browser where the global administrator
 * has been signed in before — the code screen cannot know who is signing in,
 * and teachers on shared classroom computers must never be offered it.
 */
export function markTrustOfferEligible() {
  try { window.localStorage.setItem(OFFER_KEY, '1'); } catch (err) { /* storage blocked: no offer */ }
}
export function trustOfferEligible() {
  try { return window.localStorage.getItem(OFFER_KEY) === '1'; } catch (err) { return false; }
}

/** Can this browser hold a trusted-computer key at all? */
export function trustedComputerSupported(overrides) {
  const d = deps(overrides);
  return !!d.subtle && !!(d.store && d.store.available());
}

/** The factor uids this browser holds keys for. */
export async function localFactorUids(overrides) {
  const d = deps(overrides);
  const records = await d.store.getAll();
  return new Set(records.map((r) => r.factorUid));
}

function recordUntil(record, hint) {
  const server = trustedUntilMs(hint);
  const local = Number.isFinite(record && record.expiresAt) ? record.expiresAt : 0;
  return server == null ? local : Math.min(server, local);
}

/**
 * Sign-in fast path. Firebase has asked for a second factor (`resolver` from
 * getMultiFactorResolver). If this browser holds the key of an in-date
 * trusted computer on that account, answer it and finish the sign-in.
 *
 * Returns { credential, factor } — or null when there is nothing to do or it
 * did not work, in which case the caller shows the code screen with the SAME
 * resolver (a refused code does not use it up). Never throws.
 */
export async function signInWithTrustedComputer(resolver, overrides) {
  const d = deps(overrides);
  try {
    if (!resolver || !d.subtle) return null;
    const hints = (Array.isArray(resolver.hints) ? resolver.hints : []).filter(isTrustedComputerFactor);
    if (hints.length === 0) return null;
    const records = await d.store.getAll();
    const now = d.now();
    for (const hint of hints) {
      const record = records.find((r) => r.factorUid === hint.uid);
      if (!record || !record.key) continue;
      if (now >= recordUntil(record, hint)) continue; // past its 30 days: ask for a code
      const code = await totpCode(record.key, { nowMs: now, periodSeconds: record.periodSeconds, digits: record.digits }, d.subtle);
      const assertion = d.mfa.TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
      const credential = await resolver.resolveSignIn(assertion);
      return { credential, factor: hint };
    }
    return null;
  } catch (err) {
    console.warn('Trusted computer: could not answer the code; asking for it instead.', err);
    return null;
  }
}

async function unenrollQuietly(d, user, factor, reason) {
  try {
    if (d.currentUid() !== user.uid) return false; // the session is gone
    // Removing the newest factor would sign this session out (see the header).
    if (isNewestFactor(d.mfa.multiFactor(user).enrolledFactors, factor)) return false;
    await d.mfa.multiFactor(user).unenroll(factor.uid);
    logQuietly(d, user, 'MFA_TRUSTED_COMPUTER_REMOVED', { factorName: factor.displayName || '', reason });
    return true;
  } catch (err) {
    console.warn('Trusted computer: could not remove a factor.', err);
    return false;
  }
}

/**
 * After a sign-in WITH a code: removes every trusted computer on the account
 * that is past its 30 days (whichever computer it was) — except the newest
 * factor, whose removal would sign the session out — and forgets this
 * browser's keys whose factor is gone or past its date. Never throws.
 * Resolves { removed, waiting }: display names of the factors removed, and of
 * expired ones left on the account because removing them would sign out
 * (the caller tells the administrator to remove those by hand).
 */
export async function tidyTrustedComputers(user, overrides) {
  const d = deps(overrides);
  const removed = [];
  const waiting = [];
  try {
    if (!user || d.currentUid() !== user.uid) return { removed, waiting };
    const enrolled = [...(d.mfa.multiFactor(user).enrolledFactors || [])];
    // No trusted computer on the account: nothing to do (and no reason to
    // open this browser's key store at all).
    if (!enrolled.some(isTrustedComputerFactor)) return { removed, waiting };
    const now = d.now();
    const records = await d.store.getAll();
    const mine = records.filter((r) => r.userUid === user.uid);
    for (const factor of enrolled.filter(isTrustedComputerFactor)) {
      const until = trustedUntilMs(factor);
      const record = mine.find((r) => r.factorUid === factor.uid);
      const expired = (until != null && until <= now) || (record && Number.isFinite(record.expiresAt) && record.expiresAt <= now);
      if (!expired) continue;
      if (await unenrollQuietly(d, user, factor, 'expired')) removed.push(factor.displayName || '');
      else if (factorStillEnrolled(d.mfa.multiFactor(user).enrolledFactors, factor)) waiting.push(factor.displayName || '');
    }
    const still = new Set((d.mfa.multiFactor(user).enrolledFactors || []).map((f) => f.uid));
    for (const record of mine) {
      if (!still.has(record.factorUid) || !(record.expiresAt > now)) await d.store.remove(record.factorUid);
    }
  } catch (err) {
    console.warn('Trusted computer: tidy-up failed.', err);
  }
  return { removed, waiting };
}

function friendlyError(err) {
  if (err instanceof TrustedComputerError) return err;
  const code = err && err.code;
  const messages = {
    'auth/maximum-second-factor-count-exceeded': 'Your account already has as many security factors as Firebase allows. Remove a trusted computer you no longer use in Admin → Security Settings, then try again.',
    'auth/requires-recent-login': 'Firebase needs a fresh sign-in for this. Sign out, sign in again with your code, and tick the box then.',
    'auth/operation-not-allowed': 'Authenticator-app codes are not enabled for this Firebase project.',
    'auth/second-factor-already-in-use': 'Firebase refused another authenticator-type factor on this account (auth/second-factor-already-in-use), so this computer cannot be trusted. Nothing was changed.',
  };
  return new TrustedComputerError(code || 'unknown', messages[code] || `This computer could not be trusted${code ? ` (${code})` : ''}. You will be asked for a code next time. If Firebase emails you that a factor was added, remove it in Admin → Security Settings.`);
}

/**
 * Makes this computer a trusted computer for `user` for 30 days. Call right
 * after a sign-in WITH a code. An earlier trusted-computer key on this
 * browser for the same account is replaced (its factor removed), so ticking
 * the box again renews rather than piling up factors.
 *
 * Resolves { until, name }; rejects with a TrustedComputerError whose
 * message can be shown as is.
 */
export async function trustThisComputer(user, overrides) {
  const d = deps(overrides);
  if (!user) throw new TrustedComputerError('no-user', 'Sign in first.');
  if (!d.subtle || !(d.store && d.store.available())) {
    throw new TrustedComputerError('unsupported', 'This browser cannot keep a trusted-computer key (it needs WebCrypto and IndexedDB, and private windows often block them).');
  }
  try {
    const token = await user.getIdTokenResult();
    if (!token || !token.claims || token.claims.admin !== true) {
      throw new TrustedComputerError('not-allowed', 'Only the global administrator can trust a computer.');
    }

    const earlier = (await d.store.getAll()).filter((r) => r.userUid === user.uid);

    const session = await d.mfa.multiFactor(user).getSession();
    const secret = await d.mfa.TotpMultiFactorGenerator.generateSecret(session);
    const digits = Number.isInteger(secret.codeLength) ? secret.codeLength : 6;
    const periodSeconds = Number.isInteger(secret.codeIntervalSeconds) ? secret.codeIntervalSeconds : 30;
    const bytes = base32ToBytes(secret.secretKey);
    const key = await importTotpKey(bytes, webCryptoHashName(secret.hashingAlgorithm), d.subtle);
    bytes.fill(0);

    const now = d.now();
    const name = trustedComputerName(now, d.userAgent);
    const base = { userUid: user.uid, key, digits, periodSeconds, createdAt: now, expiresAt: now + TRUST_MS };

    // Prove the store can keep the key BEFORE the account is changed (an
    // enrollment emails the account and signs its other sessions out, and
    // taking a just-added factor back off would sign this one out too).
    const pendingUid = `pending:${now}:${Math.random().toString(36).slice(2)}`;
    try {
      await d.store.put({ ...base, factorUid: pendingUid, pending: true });
    } catch (storeErr) {
      throw new TrustedComputerError('store', 'This browser could not save a trusted-computer key (private windows often block it). Nothing was changed.');
    }

    let added = null;
    try {
      if (d.currentUid() !== user.uid) {
        throw new TrustedComputerError('signed-out', 'You were signed out before this computer could be trusted. Nothing was changed.');
      }
      const code = await totpCode(key, { nowMs: now, periodSeconds, digits }, d.subtle);
      const before = new Set((d.mfa.multiFactor(user).enrolledFactors || []).map((f) => f.uid));
      await d.mfa.multiFactor(user).enroll(d.mfa.TotpMultiFactorGenerator.assertionForEnrollment(secret, code), name);
      const after = d.mfa.multiFactor(user).enrolledFactors || [];
      added = after.find((f) => !before.has(f.uid) && f.displayName === name)
        || after.find((f) => !before.has(f.uid) && isTrustedComputerFactor(f));
      if (!added) throw new TrustedComputerError('not-found', 'Firebase accepted the computer but did not list it. Check Admin → Security Settings.');
      await d.store.put({ ...base, factorUid: added.uid });
    } catch (enrollErr) {
      if (added) {
        // On the account but its key could not be saved here. Not taken back
        // off (that would sign this session out); it never answers without a
        // key, and it is removed as expired once it is not the newest factor.
        throw new TrustedComputerError('store', `This computer was added to your account but its key could not be saved here, so it will still ask for a code. You can remove "${name}" in Admin → Security Settings.`);
      }
      throw enrollErr;
    } finally {
      await d.store.remove(pendingUid);
    }
    logQuietly(d, user, 'MFA_TRUSTED_COMPUTER_ADDED', { factorName: name, until: new Date(base.expiresAt).toISOString() });

    // Renewal: this browser's earlier trusted computer for this account goes
    // now — after the new one, so it is no longer the newest factor.
    const enrolledNow = d.mfa.multiFactor(user).enrolledFactors || [];
    for (const record of earlier) {
      const factor = enrolledNow.find((f) => f.uid === record.factorUid);
      if (factor) await unenrollQuietly(d, user, factor, 'replaced');
      await d.store.remove(record.factorUid);
    }
    return { until: base.expiresAt, name };
  } catch (err) {
    throw friendlyError(err);
  }
}

/**
 * Removes a trusted computer from the account (Security Settings). This
 * browser's key for it goes FIRST, so the computer stops answering even when
 * Firebase refuses the removal. Resolves { signedOut } — true when it was the
 * newest factor and Firebase therefore ended this session. Rejects with
 * Firebase's error otherwise (see describeRemovalError).
 */
export async function removeTrustedComputer(user, factor, overrides) {
  // Not logged when the removal ends the session (the log write needs it).
  const d = deps(overrides);
  await bounded(d.store.remove(factor.uid)); // a stuck key store must not block revoking a lost computer
  try {
    await d.mfa.multiFactor(user).unenroll(factor.uid);
  } catch (err) {
    // Removed and the session ended (it was the newest factor) — or the
    // session had already ended elsewhere and nothing was removed.
    if (err && err.code === 'auth/user-token-expired' && !factorStillEnrolled(d.mfa.multiFactor(user).enrolledFactors, factor)) {
      return { signedOut: true };
    }
    throw err;
  }
  logQuietly(d, user, 'MFA_TRUSTED_COMPUTER_REMOVED', { factorName: factor.displayName || '', reason: 'removed in Security Settings' });
  return { signedOut: false };
}

/** A removal error as a sentence for Security Settings. */
export function describeRemovalError(err) {
  if (err && err.code === 'auth/requires-recent-login') {
    return 'For safety, Firebase only removes a security factor shortly after you sign in. Sign out, sign in again, and remove it then.';
  }
  return `Removal failed: ${(err && err.message) || err}`;
}
